import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from './Button.js';
import SettlementFieldList from './SettlementFieldList.js';
import SettlementModalShell from './SettlementModalShell.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import { formatIndianDate } from '../../utils/localDate.js';
import { statusBadge, statusLabel } from '../paid-amount-settlement/paidAmountSettlementUi.js';
import { mlSheetService } from '../../services/mlSheetService.js';

const money = (value) => fullCurrency(value);

export default function CompanySettlementDueView({
  kind,
  row,
  onClose,
  onSettle,
  canSettle = false,
  onViewPending,
}) {
  const [shipments, setShipments] = useState([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [shipmentsError, setShipmentsError] = useState('');

  const isPaid = kind === 'paid';
  const mlSheetId = row?.ml_sheet_id || '';
  const collectionShipmentKey = (row?.shipment_ids || []).join('|');

  useEffect(() => {
    if (isPaid || !mlSheetId) {
      setShipments([]);
      setShipmentsError('');
      return undefined;
    }
    let cancelled = false;
    setShipmentsLoading(true);
    setShipmentsError('');
    mlSheetService
      .getMLSheetViewRows(mlSheetId)
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data?.shipments) ? res.data.shipments : [];
        const allowed = new Set(
          (row?.shipment_ids || [])
            .map((sid) => String(sid || '').trim())
            .filter(Boolean),
        );
        // Prefer collection shipment ids (agent branch mapping); else API
        // already returns hub-scoped rows for assigned agents.
        const scoped = allowed.size
          ? rows.filter((s) => allowed.has(String(s.shipment_id || '').trim()))
          : rows;
        setShipments(scoped);
      })
      .catch((err) => {
        if (cancelled) return;
        setShipments([]);
        setShipmentsError(
          err?.response?.data?.error || 'Failed to load branch shipments',
        );
      })
      .finally(() => {
        if (!cancelled) setShipmentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isPaid, mlSheetId, collectionShipmentKey, row?.shipment_ids]);

  const shipmentTotals = useMemo(() => {
    let amount = 0;
    let balance = 0;
    let hasAmount = false;
    shipments.forEach((s) => {
      if (s.total_amount != null && s.total_amount !== '') {
        hasAmount = true;
        amount += Number(s.total_amount) || 0;
      }
      balance += Number(s.balance_amount) || 0;
    });
    return { amount, balance, hasAmount };
  }, [shipments]);

  const formatShipmentAmount = (value) => {
    if (value == null || value === '') return '—';
    return money(value);
  };

  if (!row) return null;

  const pendingId = row.pending_request_id;
  const pendingStatus = row.pending_request_status;
  const title = isPaid ? 'Paid remittance' : 'To-Pay remittance';
  const subtitle = isPaid ? row.shipment_id : row.ml_sheet_id;
  const outstanding = Number(row.amount_outstanding || 0);
  const netDue = Number(row.amount_due || 0);
  const returned = Number(row.amount_returned || 0);
  const toPayGross = Number(row.gross_to_pay ?? row.to_pay_freight ?? 0);
  const commissionNet = Number(
    row.net_amount != null ? row.net_amount : row.commission_amount || 0,
  );
  // Books: max(0, To-Pay − commission) = net due; outstanding = net due − returned.
  const expectedNetDue = Math.round(Math.max(0, toPayGross - commissionNet) * 100) / 100;
  const expectedOutstanding = Math.round(Math.max(0, netDue - returned) * 100) / 100;
  const collectionSettled = outstanding <= 0.009
    && String(row.collection_status || '').toUpperCase() === 'SETTLED';

  const paidItems = [
    { label: 'GC', value: row.shipment_id },
    { label: 'Date', value: formatIndianDate(row.booking_date) },
    {
      label: 'Route',
      value: [row.sender_location, row.receiver_location].filter(Boolean).join(' → ') || '—',
      span: 2,
    },
    { label: 'Payment mode', value: row.mode_of_payment || '—' },
    { label: 'Status', value: String(row.paid_remit_status || row.status || 'pending') },
    { label: 'Paid', value: money(row.paid_amount), mono: true },
    { label: 'Remitted', value: money(row.paid_remitted_amount), mono: true },
    { label: 'Outstanding', value: money(outstanding), mono: true },
  ];

  const toPayItems = [
    { label: 'ML Sheet', value: row.ml_sheet_id },
    { label: 'Date', value: formatIndianDate(row.created_at) },
    { label: 'Location', value: row.location_name || '—' },
    { label: 'Agent', value: row.agent_name || row.agent_user_id || '—' },
    {
      label: 'GCs',
      value: `${shipments.length || row.shipment_count || (row.shipment_ids || []).length || 0} GC(s)`,
    },
    { label: 'Total freight', value: money(row.total_freight), mono: true },
    { label: 'To-Pay (gross)', value: money(toPayGross), mono: true },
    {
      label: 'Commission (kept)',
      value: `${row.commission_pct != null ? `${row.commission_pct}% · ` : ''}${money(commissionNet)}`,
      mono: true,
    },
    { label: 'Net due', value: money(netDue), mono: true },
    { label: 'Returned', value: money(returned), mono: true },
    { label: 'Still to return', value: money(outstanding), mono: true },
    { label: 'Collection', value: String(row.collection_status || 'pending') },
  ];

  return (
    <SettlementModalShell
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      size={!isPaid ? 'xl' : 'lg'}
    >
      <div className="space-y-3">
        {!isPaid && toPayGross > 0 && (
          <div className={`rounded-xl border px-3 py-2.5 text-sm ${
            collectionSettled || outstanding <= 0.009
              ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
              : 'border-sky-200 bg-sky-50 text-sky-950'
          }`}
          >
            <p className="font-semibold">
              {outstanding <= 0.009
                ? `Settled — nothing left to return (${money(0)})`
                : `Still to return ${money(outstanding)}`}
            </p>
            <p className="text-xs mt-1 tabular-nums">
              To-Pay {money(toPayGross)} − commission {money(commissionNet)}
              {' = '}
              net due {money(expectedNetDue)}
              {Math.abs(expectedNetDue - netDue) > 0.02
                ? ` (books ${money(netDue)})`
                : ''}
            </p>
            <p className="text-xs mt-0.5 tabular-nums text-gray-700">
              Net due {money(netDue)} − returned {money(returned)}
              {' = '}
              still to return {money(expectedOutstanding)}
              {Math.abs(expectedOutstanding - outstanding) > 0.02
                ? ` (books ${money(outstanding)})`
                : ''}
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {!isPaid ? (
            <>
              <div className="rounded-xl border border-gray-100 bg-white p-2.5">
                <p className="text-[11px] text-gray-500">To-Pay (gross)</p>
                <p className="font-semibold tabular-nums">{money(toPayGross)}</p>
              </div>
              <div className="rounded-xl border border-violet-100 bg-violet-50/60 p-2.5">
                <p className="text-[11px] text-gray-500">Commission</p>
                <p className="font-semibold tabular-nums">{money(commissionNet)}</p>
              </div>
              <div className="rounded-xl border border-sky-100 bg-sky-50/60 p-2.5">
                <p className="text-[11px] text-gray-500">Net due</p>
                <p className="font-semibold tabular-nums">{money(netDue)}</p>
              </div>
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-2.5">
                <p className="text-[11px] text-gray-500">Still to return</p>
                <p className="font-semibold tabular-nums">{money(outstanding)}</p>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-xl border border-sky-100 bg-sky-50/60 p-2.5">
                <p className="text-[11px] text-gray-500">Outstanding</p>
                <p className="font-semibold tabular-nums">{money(outstanding)}</p>
              </div>
              <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-2.5">
                <p className="text-[11px] text-gray-500">Paid</p>
                <p className="font-semibold tabular-nums">{money(row.paid_amount)}</p>
              </div>
            </>
          )}
          {pendingStatus ? (
            <div className="rounded-xl border border-orange-100 bg-orange-50/60 p-2.5">
              <p className="text-[11px] text-gray-500">Request</p>
              <span className={`inline-flex mt-0.5 px-2 py-0.5 rounded-full text-xs border ${statusBadge(pendingStatus)}`}>
                {statusLabel(pendingStatus)}
              </span>
            </div>
          ) : null}
        </div>

        <SettlementFieldList items={isPaid ? paidItems : toPayItems} />

        {!isPaid ? (
          <div className="rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <h3 className="text-xs font-semibold text-gray-800">
                Branch shipments ({shipments.length})
              </h3>
              {shipments.length > 0 ? (
                <p className="text-[11px] text-gray-500 tabular-nums">
                  {shipmentTotals.hasAmount ? `Freight ${money(shipmentTotals.amount)} · ` : ''}
                  {shipmentTotals.balance > 0.009
                    ? `GC balance ${money(shipmentTotals.balance)}`
                    : 'GC balance cleared'}
                </p>
              ) : null}
              <p className="text-[10px] text-gray-500 sm:w-full">
                Remittance to company uses Net due / Still to return above — not GC customer balance.
              </p>
            </div>
            {shipmentsLoading ? (
              <p className="text-sm text-gray-400 text-center py-6">Loading shipments…</p>
            ) : shipmentsError ? (
              <p className="text-sm text-rose-600 text-center py-6">{shipmentsError}</p>
            ) : !shipments.length ? (
              <p className="text-sm text-gray-400 text-center py-6">No shipments for your branch on this ML</p>
            ) : (
              <>
                <div className="hidden md:block overflow-x-auto max-h-72">
                  <table className="w-full text-xs sm:text-sm">
                    <thead className="bg-white text-gray-600 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">#</th>
                        <th className="text-left px-3 py-2 font-semibold">GC</th>
                        <th className="text-left px-3 py-2 font-semibold">Sender</th>
                        <th className="text-left px-3 py-2 font-semibold">Receiver</th>
                        <th className="text-left px-3 py-2 font-semibold">Route</th>
                        <th className="text-left px-3 py-2 font-semibold">Pay</th>
                        <th className="text-right px-3 py-2 font-semibold">Amount</th>
                        <th className="text-right px-3 py-2 font-semibold">Balance</th>
                        <th className="text-left px-3 py-2 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {shipments.map((shipment, index) => (
                        <tr key={shipment.shipment_id || index} className="hover:bg-sky-50/40">
                          <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                          <td className="px-3 py-2 font-medium">
                            <Link
                              to={`/shipments/view/${shipment.shipment_id}`}
                              className="text-sky-700 hover:underline break-all"
                            >
                              {shipment.shipment_id}
                            </Link>
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-gray-800">{shipment.sender_name || '—'}</div>
                            <div className="text-[11px] text-gray-500">{shipment.sender_contact || ''}</div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-gray-800">{shipment.receiver_name || '—'}</div>
                            <div className="text-[11px] text-gray-500">{shipment.receiver_contact || ''}</div>
                          </td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                            {shipment.sender_location || 'N/A'} → {shipment.receiver_location || 'N/A'}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {shipment.mode_of_payment || shipment.payment_mode || '—'}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatShipmentAmount(shipment.total_amount)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {Number(shipment.balance_amount) > 0
                              ? money(shipment.balance_amount)
                              : 'Paid'}
                          </td>
                          <td className="px-3 py-2 text-gray-600 uppercase text-[11px]">
                            {shipment.status || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="md:hidden space-y-2 p-2 max-h-72 overflow-y-auto">
                  {shipments.map((shipment) => (
                    <Link
                      key={shipment.shipment_id}
                      to={`/shipments/view/${shipment.shipment_id}`}
                      className="block rounded-lg border border-gray-100 bg-white p-2.5"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-sky-700 text-sm break-all">{shipment.shipment_id}</p>
                        <p className="tabular-nums text-sm font-medium shrink-0">
                          {formatShipmentAmount(shipment.total_amount)}
                        </p>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {shipment.sender_location || 'N/A'} → {shipment.receiver_location || 'N/A'}
                      </p>
                      <p className="text-[11px] text-gray-600 mt-1">
                        {shipment.mode_of_payment || shipment.payment_mode || '—'}
                        {' · '}
                        {Number(shipment.balance_amount) > 0
                          ? `GC balance ${money(shipment.balance_amount)}`
                          : 'Paid'}
                      </p>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : null}

        {pendingId ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
            <p>
              A settlement request is {statusLabel(pendingStatus || 'PENDING').toLowerCase()}.
              {pendingStatus === 'HUMAN_REVIEW' ? ' An accountant will review it.' : ''}
            </p>
            {onViewPending ? (
              <button
                type="button"
                className="mt-1.5 font-semibold text-sky-800 underline"
                onClick={() => onViewPending(pendingId)}
              >
                View settlement request {pendingId}
              </button>
            ) : null}
          </div>
        ) : (
          <div className="rounded-xl border border-sky-100 bg-sky-50/60 px-2.5 py-2 text-xs text-gray-700">
            Submit from Make Settlement on this page — one receipt covers Paid and To-Pay together.
            {canSettle && onSettle ? (
              <div className="mt-2">
                <Button variant="primary" size="sm" onClick={onSettle}>
                  Make Settlement
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </SettlementModalShell>
  );
}
